import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after 2 seconds
      setTimeout(() => setShow(true), 2000);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setShow(false);
  };

  const acceptNecessaryOnly = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setShow(false);
  };

  const rejectAll = () => {
    localStorage.setItem('cookie_consent', JSON.stringify({
      necessary: true,  // Necessary cookies can't be rejected
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setShow(false);
  };

  if (!show) return null;

  if (showSettings) {
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        data-testid="cookie-settings-modal"
      >
        <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Cookie-Einstellungen</h2>
                <p className="text-sm text-muted-foreground">
                  Wählen Sie, welche Cookies Sie zulassen möchten.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(false)}
                data-testid="close-cookie-settings"
              >
                <X size={20} />
              </Button>
            </div>

            {/* Cookie Categories */}
            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-semibold">Technisch notwendig</div>
                  <div className="text-sm text-green-600 font-medium">Immer aktiv</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Diese Daten sind für die Funktion der Plattform zwingend erforderlich (Authentifizierung, Sitzungsverwaltung). Sie können nicht abgelehnt werden.
                </p>
                <div className="mt-3 text-xs text-muted-foreground">
                  Gespeichert: access_token, user_preferences
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border rounded-lg p-4 opacity-50">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-semibold">Analyse & Performance</div>
                  <div className="text-sm text-muted-foreground">Derzeit nicht aktiv</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Wir setzen derzeit KEINE Analyse-Cookies ein. Falls dies in Zukunft erfolgt, werden Sie vorher um Einwilligung gebeten.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="border rounded-lg p-4 opacity-50">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-semibold">Marketing</div>
                  <div className="text-sm text-muted-foreground">Nicht aktiv</div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Wir setzen KEINE Marketing-Cookies ein.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-8">
              <Button
                variant="outline"
                onClick={acceptNecessaryOnly}
                className="flex-1"
                data-testid="accept-necessary-only"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={acceptAll}
                className="flex-1"
                data-testid="accept-all-cookies"
              >
                Alle akzeptieren
              </Button>
            </div>

            {/* Footer Link */}
            <div className="mt-6 text-center">
              <Link 
                to="/legal/cookies" 
                className="text-sm text-blue-600 hover:underline"
              >
                Mehr über Cookies erfahren
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      data-testid="cookie-consent-banner"
    >
      {/* Dark backdrop for better visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent pointer-events-none" />
      
      <Card className="relative max-w-4xl mx-auto shadow-2xl border-2 border-slate-500/50 bg-slate-900/95 backdrop-blur-xl">
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-teal-500/20 border border-teal-500/30 flex items-center justify-center">
                <Cookie className="text-teal-400" size={24} />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-2 text-white">
                🍪 Wir respektieren Ihre Privatsphäre
              </h3>
              <p className="text-sm text-slate-300">
                Wir verwenden technisch notwendige Daten (LocalStorage) für die Funktion der Plattform. 
                Derzeit setzen wir KEINE Tracking- oder Marketing-Cookies ein. 
                Sie können Ihre Einstellungen jederzeit anpassen.
              </p>
              <div className="mt-3">
                <Link to="/legal/cookies" className="text-sm text-teal-400 hover:text-teal-300 hover:underline">
                  Mehr erfahren →
                </Link>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                size="sm"
                className="w-full md:w-auto border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white bg-slate-800/50"
                data-testid="cookie-settings-button"
              >
                <Settings size={16} className="mr-2" />
                Einstellungen
              </Button>
              <div className="flex gap-2">
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white bg-slate-800/50"
                  data-testid="reject-cookies-button"
                >
                  Ablehnen
                </Button>
                <Button
                  onClick={acceptAll}
                  size="sm"
                  className="flex-1 bg-teal-500 hover:bg-teal-400 text-white border-0 font-semibold"
                  data-testid="accept-cookies-button"
                >
                  Akzeptieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
